import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import axios from 'axios';
import { createUUID } from '@/utils/uuid';
import environment from '@/utils/environment';

const httpClient = axios.create({
    baseURL: environment.gaide_base_url,
    headers: {
        clientId: environment.gaide_client_id,
        clientSecret: environment.gaide_client_secret,
        'Content-Type': 'application/json',
        Accept: 'application/json'
    },
    timeout: 2 * 60 * 1000
});

export const useGAIDEStore = defineStore(
    'gaide',
    () => {
        const articles = ref([]);
        const isLoadingArticles = ref(false);
        const article = ref(null);
        const isLoadingArticle = ref(false);
        const scores = ref(null);
        const isLoadingScores = ref(false);
        const error = ref(null);
        const selectedArticle = ref(null);
        const isLoadingOperatorEvaluation = ref(false);

        const messages = ref([
            {
                id: createUUID(),
                message: 'Hello there! How can I help you today?',
                sender: 'bot'
            }
        ]);

        const isLoading = computed(() => isLoadingArticles.value || isLoadingArticle.value || isLoadingScores.value);

        const getArticles = async () => {
            isLoadingArticles.value = true;
            error.value = null;

            try {
                const { data } = await httpClient.get('/articles');
                articles.value = data?.articles?.map((x) => ({ name: x, value: x })) || [];
            } catch (error) {
                error.value = error;
            } finally {
                isLoadingArticles.value = false;
            }
        };

        const getArticle = async (id) => {
            isLoadingArticle.value = true;
            error.value = null;
            article.value = null;

            try {
                const { data } = await httpClient.get(`/article/${id}`);
                article.value = data
                    ? {
                          name: data.name,
                          topics: Object.keys(data.topics).map((key) => ({
                              title: data.topics[key],
                              weight: Number(data.weights[key] || 0).toLocaleString('en', { style: 'percent' }),
                              covered: false
                          }))
                      }
                    : null;
            } catch (error) {
                error.value = error;
            } finally {
                isLoadingArticle.value = false;
            }
        };

        const getScores = async (id) => {
            isLoadingScores.value = true;
            error.value = null;
            scores.value = null;

            try {
                const { data } = await httpClient.get(`/scores/${id}`);
                scores.value = data?.map((x) => ({ ...x, score: Number(x?.score || 0).toLocaleString('en', { style: 'percent' }), anomalyScore: Number(x?.anomalyScore || 0).toLocaleString('en', { style: 'percent' }) })) || null;
            } catch (error) {
                error.value = error;
            } finally {
                isLoadingScores.value = false;
            }
        };

        const setSelectedArticle = (value) => {
            selectedArticle.value = value;
        };

        const sendMessage = async (message) => {
            isLoadingOperatorEvaluation.value = true;
            error.value = null;

            messages.value.push({
                id: createUUID(),
                message,
                sender: 'me'
            });

            try {
                const body = {
                    article: selectedArticle.value,
                    declination: message,
                    operator: 'xai-dashboard-user',
                    db_insert: 'false'
                };
                const { data } = await httpClient.post('/operator-evaluation', body);

                article.value = {
                    ...article.value,
                    topics: article.value.topics.map((x, i) => ({
                        ...x,
                        covered: data?.topics[i] === true
                    }))
                };

                const score = Number(data?.score || 0).toLocaleString('en', { style: 'percent' });
                const anomalyScore = Number(data?.anomalyScore).toFixed(2);
                const coveredTopics = Object.values(data?.topics).filter((x) => x === true)?.length || 0;
                const totalTopics = Object.values(data?.topics)?.length || 0;

                messages.value.push({
                    id: createUUID(),
                    message: `Score: ${score} (sum weight of covered topics)\nAnomaly Score: ${anomalyScore} (anomalous if close to or greater than 1)\n${coveredTopics} topics out of ${totalTopics} covered\n${data?.comment}`,
                    sender: 'xai-dashboard-user'
                });
            } catch (error) {
                error.value = error;
            } finally {
                isLoadingOperatorEvaluation.value = false;
            }
        };

        const resetState = () => {
            isLoadingArticles.value = false;
            article.value = null;
            isLoadingArticle.value = false;
            scores.value = null;
            isLoadingScores.value = false;
            error.value = null;
            selectedArticle.value = null;
            isLoadingOperatorEvaluation.value = false;
            messages.value = [
                {
                    id: createUUID(),
                    message: 'Hello there! How can I help you today?',
                    sender: 'bot'
                }
            ];
        };

        return { isLoading, isLoadingOperatorEvaluation, articles, article, scores, error, selectedArticle, messages, getArticles, getArticle, getScores, sendMessage, setSelectedArticle, resetState };
    },
    {
        persist: true
    }
);
